import { faMinus, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@ui';
import { cx } from 'class-variance-authority';
import { useClubSlug } from 'components/Clubs/hooks';
import Loader from 'components/Loader';
import moment from 'moment';
import { useCallback } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { Addon } from '../types';

interface Props {
    clubSlug?: string;
}

interface ExtendedAddon extends Addon {
    available?: number;
}

const AddonSelection = ({ clubSlug: _clubSlug }: Props) => {
    const { t } = useTranslation('space-manager');
    const { watch } = useFormContext();
    const [start, end] = watch(['start', 'end']);
    const clubSlug = useClubSlug(_clubSlug);
    const {
        field: { value, onChange }
    } = useController({
        name: `addons`
    });
    const { data, isLoading } = useSWR<{ data: ExtendedAddon[] }>(
        clubSlug && start && end
            ? `club/${clubSlug}/space-manager/addon?start=${moment(start).toISOString()}&end=${moment(
                  end
              ).toISOString()}`
            : null
    );

    const isSelected = useCallback(
        (addon: ExtendedAddon) => {
            return value?.find((selectedAddon: ExtendedAddon) => selectedAddon.id === addon.id)?.quantity as
                | undefined
                | number;
        },
        [value]
    );

    const handleAdd = useCallback(
        (addon: ExtendedAddon) => {
            const quantity = isSelected(addon);

            if (addon.available && quantity >= addon.available) {
                return;
            }

            if (quantity > 0) {
                onChange(
                    (value ?? []).map((selectedAddon: ExtendedAddon) =>
                        selectedAddon.id === addon.id ? { ...selectedAddon, quantity: quantity + 1 } : selectedAddon
                    )
                );
            } else {
                onChange([...(value ?? []), { id: addon.id, quantity: 1 }]);
            }
        },
        [value, isSelected, onChange]
    );

    const handleRemove = useCallback(
        (addon: ExtendedAddon) => {
            const quantity = isSelected(addon);

            if (quantity - 1 <= 0) {
                onChange((value ?? []).filter((selectedAddon: ExtendedAddon) => selectedAddon.id !== addon.id));
            } else {
                onChange(
                    (value ?? []).map((selectedAddon: ExtendedAddon) =>
                        selectedAddon.id === addon.id ? { ...selectedAddon, quantity: quantity - 1 } : selectedAddon
                    )
                );
            }
        },
        [value, isSelected, onChange]
    );

    return (
        <Loader isLoading={isLoading}>
            <div className="tw-space-y-4">
                {data?.data?.map((addon) => (
                    <div
                        className={cx(
                            addon.available > 0 && 'tw-cursor-pointer tw-transition-colors hover:tw-bg-accents-2',
                            'tw-card-flat'
                        )}
                        key={addon.id}
                        onClick={() => addon.available > 0 && handleAdd(addon)}
                    >
                        <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
                            <h4>{addon.name}</h4>
                            <div
                                className={cx(
                                    isSelected(addon) && 'tw-border-success tw-bg-success tw-text-white',
                                    'tw-flex tw-h-8 tw-w-16 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-border-accents-6 tw-text-accents-6 tw-transition-colors'
                                )}
                            >
                                <span className="tw-font-semibold">
                                    {isSelected(addon) ?? 0} / {addon.available ?? 1}
                                </span>
                            </div>
                        </div>
                        <p className="tw-text-xs tw-text-accents-6">{addon.description}</p>
                        <div className="ta-gap-4 tw-flex tw-items-center tw-justify-between">
                            <span className="tw-block tw-text-center tw-text-xs tw-italic tw-text-accents-6">
                                {addon.available > 0 ? t('addon.selection.clickToAdd') : t('addon.selection.notAvailable')}
                            </span>
                            <div className="tw-flex tw-gap-2">
                                <Button
                                    type="button"
                                    onPress={() => handleAdd(addon)}
                                    disabled={addon.available === 0 || isSelected(addon) >= addon.available}
                                    size="xs"
                                    color="success"
                                >
                                    <FontAwesomeIcon icon={faPlus} />
                                </Button>
                                <Button
                                    type="button"
                                    onPress={() => handleRemove(addon)}
                                    disabled={!(isSelected(addon) > 0)}
                                    size="xs"
                                    color="error"
                                >
                                    <FontAwesomeIcon icon={faMinus} />
                                </Button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            <Button
                block
                disabled={(value ?? [])?.length <= 0}
                onPress={() => onChange([])}
                className="tw-mt-4"
                size="xs"
                color="warning"
            >
                {t('addon.selection.resetSelection')}
            </Button>
        </Loader>
    );
};

export default AddonSelection;
